import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/zeit/45b021a8/node_modules/gatsby-theme-carbon/src/templates/Default.js";
import Preview from 'components/Preview';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const AnchorLinks = makeShortcode("AnchorLinks");
const AnchorLink = makeShortcode("AnchorLink");
const InlineNotification = makeShortcode("InlineNotification");
const Caption = makeShortcode("Caption");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h3>{`With two pages comprised entirely of Carbon components, let’s revisit the landing page and build a couple components of our own by using Carbon icons and tokens.`}</h3>
    <AnchorLinks mdxType="AnchorLinks">
      <AnchorLink mdxType="AnchorLink">Fork, clone and branch</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Review design</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Create components</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Use components</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Add styling</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Check accessibility</AnchorLink>
      <AnchorLink mdxType="AnchorLink">Submit pull request</AnchorLink>
    </AnchorLinks>
    <h2>{`Preview`}</h2>
    <p>{`Carbon provides a solid foundation for building web applications through its color palette, layout, spacing, type, as well as common building blocks in the form of components. So far, we’ve only used Carbon components to build out two pages.`}</p>
    <p>{`Next, we’re going to use Carbon assets to build application-specific components. We’ll create our components with considerations for accessibility and responsiveness.`}</p>
    <p>{`A `}<a parentName="p" {...{
        "href": "https://angular-step-5-carbon-tutorial.netlify.com"
      }}>{`preview`}</a>{` of what you’ll build (see bottom of page):`}</p>
    <Preview height="400" title="Carbon Angular Tutorial Step 4" src="https://angular-step-5-carbon-tutorial.netlify.com" frameBorder="no" allowtransparency="true" allowFullScreen="true" mdxType="Preview" />
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` If you get lint errors when you copy the code from the snippets, run `}<inlineCode parentName="p">{`ng lint --fix`}</inlineCode>{` to fix them.`}</p>
    </InlineNotification>
    <h2>{`Fork, clone and branch`}</h2>
    <p>{`This tutorial has an accompanying GitHub repository called `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-tutorial-angular"
      }}>{`carbon-tutorial-angular`}</a>{` that we’ll use as a starting point for each step. If you haven’t forked and cloned that repository yet, and haven’t added the upstream remote, go ahead and do so by following the `}<a parentName="p" {...{
        "href": "/tutorial/angular/step-1#fork-clone--branch"
      }}>{`step 1 instructions`}</a>{`.`}</p>
    <h3>{`Branch`}</h3>
    <p>{`With your repository all set up, let’s check out the branch for this tutorial step’s starting point.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git fetch upstream
git checkout -b angular-step-4 upstream/angular-step-4
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` This builds on top of step 3, but be sure to check out the upstream step 4 branch because it includes the static assets required to get through this step.`}</p>
    </InlineNotification>
    <h3>{`Build and start app`}</h3>
    <p>{`Install the app’s dependencies (in case you’re starting fresh in your current directory and not continuing from the previous step):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install
`}</code></pre>
    <p>{`Then, start the app:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm start
`}</code></pre>
    <p>{`You should see something similar to where the `}<a parentName="p" {...{
        "href": "/tutorial/angular/step-3"
      }}>{`previous step`}</a>{` left off.`}</p>
    <h2>{`Review design`}</h2>
    <p>{`Here’s what we’re building – an informational section that has a heading and three subheadings. Each subheading has accompanying copy and a pictogram. We’ll assume that this informational section is used elsewhere on the site, meaning it’s a great opportunity to build it as a resusable component. As for naming, we’ll call it an `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` with three `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{`s as children.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "36.24454148471616%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAABRElEQVQoz1VR2XKDMAzk/78stM1MmoMALU04DCEnxjZpnrYrZTrTPmhsrVerlRXdncNkGtwPHR79AY/jUe9Ta3hK3uObuGChbRV/9MSIT8wD80lqyb2PIyJ3u6FPtxj2O7i6hqP4pSjQrFfo0hS2LIlXOH7kqFfEtgk8OQNxs1mjfF/AJBu4qoS/XhEFaxGaRkmuqRG6Fn2WIotnyN9eVSDwbc/CdEbsJWbzPQYKCCeLY5TLJTzNeJqLwjBQvYKnmAZHuO12OOU5zl+Fuhb8wCYmSRQfKWYZxXyOmpOci08KVn8EJRGXzXNkcTsZo25/cS85mwWeOg1DJpM/Vd4/QZlfnAjIggs7XulOChVjyL9a5T2nkfspzzSXr/LypoKjheO2PDflug6eGx0pKiF33wnewtKJI0dy4Y48rTSUOm5YNWjuBxvABosCsXIDAAAAAElFTkSuQmCC')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Info section layout",
        "title": "Info section layout",
        "src": "/static/b545e95ae155163f1ab04c2711c9cb30/3cbba/info-layout.png",
        "srcSet": ["/static/b545e95ae155163f1ab04c2711c9cb30/7fc1e/info-layout.png 288w", "/static/b545e95ae155163f1ab04c2711c9cb30/a5df1/info-layout.png 576w", "/static/b545e95ae155163f1ab04c2711c9cb30/3cbba/info-layout.png 1152w", "/static/b545e95ae155163f1ab04c2711c9cb30/0b124/info-layout.png 1728w", "/static/b545e95ae155163f1ab04c2711c9cb30/4ea69/info-layout.png 2304w", "/static/b545e95ae155163f1ab04c2711c9cb30/02d7b/info-layout.png 2748w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <Caption mdxType="Caption">Info section layout</Caption>
    <h2>{`Create components`}</h2>
    <p>{`First we need files for the components, so create an `}<inlineCode parentName="p">{`Info`}</inlineCode>{` folder in `}<inlineCode parentName="p">{`src/components`}</inlineCode>{`. Even though we’re building multiple components, their names all start with `}<inlineCode parentName="p">{`Info`}</inlineCode>{`, so it makes sense to have them share one folder in components. Create these files:`}</p>
    <h3>{`Generate modules & components using the Angular CLI`}</h3>
    <p>{`Let’s generate a new module that’ll handle our `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` and `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` components:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng generate module info
`}</code></pre>
    <p>{`Now we generate our `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` and `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` components:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng generate component info/info-card --lint-fix
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`ng generate component info/info-section --lint-fix
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` The `}<inlineCode parentName="p">{`--lint-fix`}</inlineCode>{` flag will automatically resolve lint issues in the newly generated files.`}</p>
    </InlineNotification>
    <p>{`Running the above commands should get you the folder structure below:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`src/app/info
└──info.module.ts
└──info-card
    ├──info-card.component.scss
    ├──info-card.component.ts
    ├──info-card.component.spec.ts
    └──info-card.component.html
└──info-section
    ├──info-section.component.scss
    ├──info-section.component.ts
    ├──info-section.component.spec.ts
    └──info-section.component.html
`}</code></pre>
    <p>{`Now let’s create a JSON file that’ll include our content to be shown in the components:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "path=src/app/info/info.json",
        "path": "src/app/info/info.json"
      }}>{`{
  "title": "The Principles",
  "items": [
    {
      "heading": "Carbon is Open",
      "content": "It's a distributed effort, guided by the principles of the open-source movement. Carbon's users are also it's makers, and everyone is encouraged to contribute."
    },
    {
      "heading": "Carbon is Modular",
      "content": "Carbon's modularity ensures maximum flexibility in execution. It's components are designed to work seamlessly with each other, in whichever combination suits the needs of the user."
    },
    {
      "heading": "Carbon is Consistent",
      "content": "Based on the comprehensive IBM Design Language, every element and component of Carbon was designed from the ground up to work elegantly together to ensure consistent, cohesive user experiences."
    }
  ]
}
`}</code></pre>
    <p>{`Now we also need to tell typescript to resolve JSON files as modules when compiling:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "path=src/tsconfig.app.json",
        "path": "src/tsconfig.app.json"
      }}>{`{
    "compilerOptions": {
        "sourceMap": true,
        "declaration": false,
        ...
        "resolveJsonModule": true
    },
    ...
}
`}</code></pre>
    <h3>{`InfoSection component`}</h3>
    <p>{`Let’s create the parent component that includes the “The Principles” heading. That markup currently looks like this in `}<inlineCode parentName="p">{`landing-page.component.html`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/home/landing-page/landing-page.component.html",
        "path": "src/app/home/landing-page/landing-page.component.html"
      }}>{`<div ibmRow class="landing-page__r3">
  <div ibmCol [columnNumbers]="{'md': 4, 'lg': 4}">
    <h3 class="landing-page__label">The Principles</h3>
  </div>
  <div ibmCol [columnNumbers]="{'md': 4, 'lg': 4}">Carbon is Open</div>
  <div ibmCol [columnNumbers]="{'md': 4, 'lg': 4}">Carbon is Modular</div>
  <div ibmCol [columnNumbers]="{'md': 4, 'lg': 4}">Carbon is Consistent</div>
</div>
`}</code></pre>
    <p>{`We want to do a few things when abstracting it to a component. Firstly, we want to encapsulate the component’s styles within it’s dedicated stylesheet. We don’t want to include `}<inlineCode parentName="p">{`landing-page__r3`}</inlineCode>{` within the component template as that’s specific to the landing page. In order to allow the landing page to have access to styling this specific instance of an `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` component we could pass the class into the instance of the component. However, we won’t need to do any additional styling from the `}<inlineCode parentName="p">{`landing-page`}</inlineCode>{` so we can just go ahead use the component as is.`}</p>
    <p>{`We’ll also:`}</p>
    <ul>
      <li parentName="ul">{`Add component class names like `}<inlineCode parentName="li">{`.info-section`}</inlineCode>{` and `}<inlineCode parentName="li">{`.info-section__heading`}</inlineCode></li>
      <li parentName="ul">{`Semantically use `}<inlineCode parentName="li">{`<section>`}</inlineCode>{` instead of `}<inlineCode parentName="li">{`<div>`}</inlineCode></li>
      <li parentName="ul">{`Update the grid columns to match the design`}</li>
      <li parentName="ul">{`Update the component content to use `}<inlineCode parentName="li">{`info.json`}</inlineCode></li>
      <li parentName="ul">{`Replace columns 2 - 4 with `}<inlineCode parentName="li">{`InfoCard`}</inlineCode>{` components`}</li>
      <li parentName="ul">{`Remove class styling from `}<inlineCode parentName="li">{`landing-page`}</inlineCode>{` component`}</li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/home/landing-page/landing-page.component.html",
        "path": "src/app/home/landing-page/landing-page.component.html"
      }}>{`<app-info-section></app-info-section>
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/info/info-section/info-section.component.html",
        "path": "src/app/info/info-section/info-section.component.html"
      }}>{`<section ibmRow class="info-section info-section__r1">
  <div ibmCol [columnNumbers]="{'md': 8, 'lg': 4}">
    <h3 class="info-section__heading">The Principles</h3>
  </div>
  <app-info-card ibmCol [columnNumbers]="{'md': 4, 'lg': 4}"> </app-info-card>
  <app-info-card ibmCol [columnNumbers]="{'md': 4, 'lg': 4}"> </app-info-card>
  <app-info-card ibmCol [columnNumbers]="{'md': 4, 'lg': 4}"> </app-info-card>
</section>
`}</code></pre>
    <p>{`As you can see we’ve added instances of the `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` component. There is a few things that we need to do to resolve the errors in the browser. Firstly we need to update `}<inlineCode parentName="p">{`InfoModule`}</inlineCode>{` to be able to use the `}<inlineCode parentName="p">{`GridModule`}</inlineCode>{`. At the same time we will export the `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` and `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` components from the `}<inlineCode parentName="p">{`InfoModule`}</inlineCode>{` so that we can use them in the `}<inlineCode parentName="p">{`HomeModule`}</inlineCode>{` components (specifically `}<inlineCode parentName="p">{`landing-page`}</inlineCode>{`).`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts",
        "metastring": "path=src/app/info/info.module.ts",
        "path": "src/app/info/info.module.ts"
      }}>{`import { NgModule } from "@angular/core";
...
import { GridModule } from "carbon-components-angular";


@NgModule({
    declarations: [InfoCardComponent, InfoSectionComponent],
    imports: [
        CommonModule,
        GridModule
    ],
    exports:  [InfoCardComponent, InfoSectionComponent]
})
export class InfoModule { }

`}</code></pre>
    <p>{`We also need to import `}<inlineCode parentName="p">{`InfoModule`}</inlineCode>{` into `}<inlineCode parentName="p">{`HomeModule`}</inlineCode>{` otherwise we cannot use the `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` component within `}<inlineCode parentName="p">{`landing-page`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts",
        "metastring": "path=src/app/home/home.module.ts",
        "path": "src/app/home/home.module.ts"
      }}>{`...
import { InfoModule } from "./../info/info.module";
import {
    BreadcrumbModule,
    ButtonModule,
    GridModule,
    TabsModule
} from "carbon-components-angular";

@NgModule({
    declarations: [LandingPageComponent],
    imports: [
        CommonModule,
    ...
        TabsModule,
        InfoModule
    ]
})
export class HomeModule { }
`}</code></pre>
    <p>{`Now we’re going to go into the `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` component to add content from `}<inlineCode parentName="p">{`info.json`}</inlineCode>{`.`}</p>
    {
      /* prettier-ignore-end */
    }
    <pre><code parentName="pre" {...{
        "className": "language-ts",
        "metastring": "path=src/app/info/info-section/info-section.component.ts",
        "path": "src/app/info/info-section/info-section.component.ts"
      }}>{`import { Component, OnInit } from '@angular/core';
import * as data from '../info.json';

@Component({
  selector: 'app-info-section',
  templateUrl: './info-section.component.html',
  styleUrls: ['./info-section.component.scss'],
})
export class InfoSectionComponent implements OnInit {
  heading = data.title;
  items = data.items;

  constructor() {}

  ngOnInit() {}
}
`}</code></pre>
    {
      /* prettier-ignore-start */
    }
    <p>{`We essentially have added a `}<inlineCode parentName="p">{`heading`}</inlineCode>{` and `}<inlineCode parentName="p">{`items`}</inlineCode>{` variable into the model of `}<inlineCode parentName="p">{`InfoSectionComponent`}</inlineCode>{`, which we’ll be using later to pass content into the children components `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{`.`}</p>
    <p>{`We’ve also added class names that are specific to this component that we’ll target in our stylesheet:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-scss",
        "metastring": "path=src/app/info/info-section/info-section.component.scss",
        "path": "src/app/info/info-section/info-section.component.scss"
      }}>{`@import "~carbon-components/scss/globals/scss/typography";
@import "~carbon-components/scss/globals/scss/vendor/@carbon/type/scss/font-family";

@mixin info-section-background() {
  background-color: $ui-01;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -$spacing-05;
    top: 0;
    right: -$spacing-05;
    bottom: 0;
    background: $ui-01;
    z-index: -1;
  }
}

.info-section {
  @include info-section-background;
  display: flex;
  height: fit-content;

  &.info-section__r1 {
    padding-top: $spacing-09;
    padding-bottom: $spacing-09;
    height: auto;
  }

  .info-section__heading {
    @include carbon--type-style("heading-01");
    @include carbon--font-weight("semibold");
  }
}
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <h3>{`InfoCard component`}</h3>
    <p>{`Next up we’re going to build a component for columns 2 - 4, which currently looks like `}<inlineCode parentName="p">{`<div ibmCol [columnNumbers]="{'md': 4, 'lg': 4}">Carbon is Open</div>`}</inlineCode>{`. We’ve added the data into the parent component but we have not told `}<inlineCode parentName="p">{`snfo-section`}</inlineCode>{` component to give any data to the `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` because we first need to tell `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` that it will have data to display. In `}<inlineCode parentName="p">{`src/app/info/info-card/info-card.component.html`}</inlineCode>{`, add:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/info/info-card/info-card.component.html",
        "path": "src/app/info/info-card/info-card.component.html"
      }}>{`<div class="info-card">
  <h4 class="info-card__heading">
    {{heading}}
  </h4>
  <div class="info-card__body">{{content}}</div>
</div>
`}</code></pre>
    <p>{`Now we’ve setup our `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` to display a heading and some content. We will setup the component `}<inlineCode parentName="p">{`.ts`}</inlineCode>{` file to accept a `}<inlineCode parentName="p">{`heading`}</inlineCode>{` and `}<inlineCode parentName="p">{`content`}</inlineCode>{` input to be displayed.`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-ts",
        "metastring": "path=src/app/info/info-card/info-card.component.ts",
        "path": "src/app/info/info-card/info-card.component.ts"
      }}>{`import { Component, OnInit, Input } from "@angular/core";

@Component({
  selector: "app-info-card",
  templateUrl: "./info-card.component.html",
  styleUrls: ["./info-card.component.scss"],
})
export class InfoCardComponent implements OnInit {
  @Input() heading;
  @Input() content;

  constructor() {}

  ngOnInit() {}
}
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`Finally, we’ll update `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` to use the new `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/info/info-section/info-section.component.html",
        "path": "src/app/info/info-section/info-section.component.html"
      }}>{`<section ibmRow class="info-section info-section__r1">
  <div ibmCol [columnNumbers]="{'md': 4, 'lg': 4}">
    <h3 class="info-section__heading">{{heading}}</h3>
  </div>
  <app-info-card
    ibmCol
    [columnNumbers]="{'md': 4, 'lg': 4}"
    [heading]="items[0].heading"
    [content]="items[0].content"
  >
  </app-info-card>
  <app-info-card
    ibmCol
    [columnNumbers]="{'md': 4, 'lg': 4}"
    [heading]="items[1].heading"
    [content]="items[1].content"
  >
  </app-info-card>
  <app-info-card
    ibmCol
    [columnNumbers]="{'md': 4, 'lg': 4}"
    [heading]="items[2].heading"
    [content]="items[2].content"
  >
  </app-info-card>
</section>
`}</code></pre>
    <h2>{`Use components`}</h2>
    <p>{`Next we’re going to use Carbon Icon components in our custom components. We’re going to place the icons within the `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` components as children. In the `}<inlineCode parentName="p">{`InfoModule`}</inlineCode>{` we will import the modules we need for the Carbon Angular Icons:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-ts",
        "metastring": "path=src/info/info.module.ts",
        "path": "src/info/info.module.ts"
      }}>{`import { NgModule } from "@angular/core";
...

import { PersonFavorite32Module } from "@carbon/icons-angular/lib/person--favorite/32";
import { Globe32Module } from "@carbon/icons-angular/lib/globe/32";
import { Application32Module } from "@carbon/icons-angular/lib/application/32";

import { GridModule } from "carbon-components-angular";

@NgModule({
  declarations: [InfoSectionComponent, InfoCardComponent],
  imports: [
    ...
    PersonFavorite32Module,
    Globe32Module,
    Application32Module,
  ],
  exports: [InfoSectionComponent, InfoCardComponent],
})
export class InfoModule {}
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`Next we will use `}<inlineCode parentName="p">{`<ng-content>`}</inlineCode>{` so that we can inject icons into the `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` from it’s parent component (`}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{`)`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/info/info-card/info-card.component.html",
        "path": "src/app/info/info-card/info-card.component.html"
      }}>{`<div class="info-card">
  ...
  <div class="info-card__body">{{content}}</div>
  <div class="info-card__icon">
    <ng-content></ng-content>
  </div>
</div>
`}</code></pre>
    <p>{`We also need to update `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` with the icon components:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/info/info-section/info-section.component.html",
        "path": "src/app/info/info-section/info-section.component.html"
      }}>{`<section ibmRow class="info-section info-section__r1">
  <div ibmCol [columnNumbers]="{'md': 4, 'lg': 4}">
    <h3 class="info-section__heading">{{heading}}</h3>
  </div>
  <app-info-card
    ibmCol
    [columnNumbers]="{'md': 4, 'lg': 4}"
    [heading]="items[0].heading"
    [content]="items[0].content"
  >
    <ibm-icon-person-favorite32></ibm-icon-person-favorite32>
  </app-info-card>
  <app-info-card
    ibmCol
    [columnNumbers]="{'md': 4, 'lg': 4}"
    [heading]="items[1].heading"
    [content]="items[1].content"
  >
    <ibm-icon-application32></ibm-icon-application32>
  </app-info-card>
  <app-info-card
    ibmCol
    [columnNumbers]="{'md': 4, 'lg': 4}"
    [heading]="items[2].heading"
    [content]="items[2].content"
  >
    <ibm-icon-globe32></ibm-icon-globe32>
  </app-info-card>
</section>
`}</code></pre>
    <h2>{`Add styling`}</h2>
    <p>{`We currently have the components displaying the content we created. We need to add more styling for Carbon breakpoints, icon alignments, as well as the `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` title styling.`}</p>
    <span {...{
      "className": "gatsby-resp-image-wrapper",
      "style": {
        "position": "relative",
        "display": "block",
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1152px"
      }
    }}>{`
      `}<span parentName="span" {...{
        "className": "gatsby-resp-image-background-image",
        "style": {
          "paddingBottom": "36.24454148471616%",
          "position": "relative",
          "bottom": "0",
          "left": "0",
          "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAHCAYAAAAIy204AAAACXBIWXMAAAsSAAALEgHS3X78AAABMUlEQVQoz12RT1ODMBDF+eR+Iu+eHQ+Oh844HYu0tpQ/BQRKgASSYHWeu6kwHQ8Pkh/J28eud04t3h4spouG0RbfMAhfJsSvBl8/I6yZYIyB1hrDMGAciVnrGK+Z8beZeaJS2K5KtJ1A30uMVsJ/EvBXCYrPE9q2Jd6jKApEUYQsy6CUgiCeJAnCMHRvKaXjntYjzDS4DUOumGc5gq2PIAgWg8PhgM1m45gQwhXa+T7eSeHx6M7wXY8fnEwquVQRrUBZlqjremF5niNNU8f7rkNDhs/bHfZxjLqqljDOcL40p7xWu/bnNrnbkwyxE7E7atNaDbiMFOq/4Sw24GRN0yxmLGYdJbuaK1TnDo/7AiX92fB3xhnypG4TsngIrNvUs5mUCpoGl38orO954sqxuYe/C4kRXhAvaZgAAAAASUVORK5CYII=')",
          "backgroundSize": "cover",
          "display": "block"
        }
      }}></span>{`
  `}<img parentName="span" {...{
        "className": "gatsby-resp-image-image",
        "alt": "Info section spacing",
        "title": "Info section spacing",
        "src": "/static/2ea2681b6eeb1a14dd7c9dc91ec8724e/3cbba/info-spacing.png",
        "srcSet": ["/static/2ea2681b6eeb1a14dd7c9dc91ec8724e/7fc1e/info-spacing.png 288w", "/static/2ea2681b6eeb1a14dd7c9dc91ec8724e/a5df1/info-spacing.png 576w", "/static/2ea2681b6eeb1a14dd7c9dc91ec8724e/3cbba/info-spacing.png 1152w", "/static/2ea2681b6eeb1a14dd7c9dc91ec8724e/0b124/info-spacing.png 1728w", "/static/2ea2681b6eeb1a14dd7c9dc91ec8724e/4ea69/info-spacing.png 2304w", "/static/2ea2681b6eeb1a14dd7c9dc91ec8724e/02d7b/info-spacing.png 2748w"],
        "sizes": "(max-width: 1152px) 100vw, 1152px",
        "style": {
          "width": "100%",
          "height": "100%",
          "margin": "0",
          "verticalAlign": "middle",
          "position": "absolute",
          "top": "0",
          "left": "0"
        },
        "loading": "lazy"
      }}></img>{`
    `}</span>
    <h3>{`Layout`}</h3>
    <p>{`Starting with layout, add the following to `}<inlineCode parentName="p">{`src/app/info/info-card/info-card.component.scss`}</inlineCode>{`.`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-scss",
        "metastring": "path=src/app/info/info-card/info-card.component.scss",
        "path": "src/app/info/info-card/info-card.component.scss"
      }}>{`@import "~carbon-components/scss/globals/scss/typography";
@import "~carbon-components/scss/globals/scss/layout";

.info-card {
  display: flex;
  flex-direction: column;

  .info-card__heading {
    @include carbon--type-style("productive-heading-03");
  }

  .info-card__body {
    margin-top: $spacing-06;
    flex-grow: 1;
    @include type-style("body-long-01");

    // prevent large line lengths between small and medium viewports
    @include carbon--breakpoint-between(321px, md) {
      max-width: 75%;
    }
  }
}
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`Once you save, go ahead and resize your browser to see the responsive layout at the different breakpoints. Make sure to review these color and spacing tokens. There are also a few breakpoint mixins that may be new to you. The `}<inlineCode parentName="p">{`@carbon/layout`}</inlineCode>{` `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon/blob/master/packages/layout/docs/sass.md"
      }}>{`SassDoc`}</a>{` is a great reference to see what all is available.`}</p>
    <h3>{`Type`}</h3>
    <p>{`Our `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` headings have to be updated to match the specs. We need to make the last word of each heading bold. Add the below custom function (`}<inlineCode parentName="p">{`createArrayFromPhrase`}</inlineCode>{`) to `}<inlineCode parentName="p">{`src/app/info/info-card/info-card.component.ts`}</inlineCode>{`:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-ts",
        "metastring": "path=src/app/info/info-card/info-card.component.ts",
        "path": "src/app/info/info-card/info-card.component.ts"
      }}>{`import { Component, OnInit, Input } from '@angular/core';

@Component({
  selector: "app-info-card",
  templateUrl: "./info-card.component.html",
  styleUrls: ["./info-card.component.scss"],
})
export class InfoCardComponent implements OnInit {
  @Input() heading;
  @Input() content;
  splitHeading;

  constructor() {}

  ngOnInit() {
    // create the split title to get bold styles
    this.splitHeading = this.createArrayFromPhrase(this.heading);
  }

  // Take in a phrase and separate the third word in an array
  createArrayFromPhrase(phrase) {
    const splitPhrase = phrase.split(" ");
    const thirdWord = splitPhrase.pop();
    return [splitPhrase.join(" "), thirdWord];
  }
}
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`This’ll allow us to still accept `}<inlineCode parentName="p">{`heading`}</inlineCode>{` as an input to the component, but we’ll need to change the template to use `}<inlineCode parentName="p">{`splitHeading`}</inlineCode>{` rather than `}<inlineCode parentName="p">{`heading`}</inlineCode>{` to display our heading content.`}</p>
    <p>{`Update the component template to use `}<inlineCode parentName="p">{`splitHeading`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "path=src/app/info/info-card/info-card.component.html",
        "path": "src/app/info/info-card/info-card.component.html"
      }}>{`<div class="info-card">
  <h4 class="info-card__heading">
    {{splitHeading[0]}}
    <strong>{{splitHeading[1]}}</strong>
  </h4>
  <div class="info-card__body">{{content}}</div>
  <div class="info-card__icon">
    <ng-content></ng-content>
  </div>
</div>
`}</code></pre>
    <p>{`Then, add some styling for the icons within our `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` component:`}</p>
    {
      /* prettier-ignore-start */
    }
    <pre><code parentName="pre" {...{
        "className": "language-scss",
        "metastring": "path=src/app/info/info-card/info-card.component.scss",
        "path": "src/app/info/info-card/info-card.component.scss"
      }}>{`@import "~carbon-components/scss/globals/scss/typography";
@import "~carbon-components/scss/globals/scss/layout";

.info-card {
  ... .info-card__icon {
    margin-top: $spacing-09;
  }
}
`}</code></pre>
    {
      /* prettier-ignore-end */
    }
    <p>{`Finally, we need styles for the `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{` borders. We will use the `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` to apply styles using the children selectors. Update the `}<inlineCode parentName="p">{`InfoSection`}</inlineCode>{` stylesheet to include styles targeting the `}<inlineCode parentName="p">{`InfoCard`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scss",
        "metastring": "path=src/app/info/info-section/info-section.component.scss",
        "path": "src/app/info/info-section/info-section.component.scss"
      }}>{`.info-section {
  ... app-info-card {
    margin-top: $spacing-09;
  }

  // top border in only small breakpoints to prevent overrides
  @include carbon--breakpoint-down(md) {
    app-info-card:not(:nth-child(2)) {
      border-top: 1px solid $ui-03;
      padding-top: $spacing-09;
    }
  }

  // left border in just the 2nd column items
  @include carbon--breakpoint(md) {
    app-info-card:nth-child(odd) {
      border-left: 1px solid $ui-03;
    }
  }

  // left border in all items
  @include carbon--breakpoint(lg) {
    app-info-card {
      margin-top: 0;
      border-left: 1px solid $ui-03;
    }
  }
}
`}</code></pre>
    <h2>{`Submit pull request`}</h2>
    <p>{`We’re going to submit a pull request to verify completion of this tutorial step.`}</p>
    <h3>{`Git commit and push`}</h3>
    <p>{`Before we can create a pull request, stage and commit all of your changes:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git add --all && git commit -m "feat(tutorial): complete step 4"
`}</code></pre>
    <p>{`Then, push to your repository:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`git push origin angular-step-4
`}</code></pre>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Having issues pushing your changes? `}<a parentName="p" {...{
          "href": "/tutorial/angular/step-1#git-commit-and-push"
        }}>{`Step 1`}</a>{` has troubleshooting notes that may help.`}</p>
    </InlineNotification>
    <h3>{`Pull request (PR)`}</h3>
    <p>{`Finally, visit `}<a parentName="p" {...{
        "href": "https://github.com/carbon-design-system/carbon-tutorial"
      }}>{`carbon-tutorial`}</a>{` to “Compare & pull request”. In doing so, make sure that you are comparing to `}<inlineCode parentName="p">{`angular-step-4`}</inlineCode>{` into `}<inlineCode parentName="p">{`base: angular-step-4`}</inlineCode>{`.`}</p>
    <InlineNotification mdxType="InlineNotification">
      <p><strong parentName="p">{`Note:`}</strong>{` Expect your tutorial step PRs to be reviewed by the Carbon team but not merged. We’ll close your PR so we can keep the repository’s remote branches pristine and ready for the next person!`}</p>
    </InlineNotification>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      